import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES, ROLES } from '../utils/constants';
import ApiTester from '../components/admin/ApiTester';
import RestCommandTool from '../components/admin/RestCommandTool';
import MainLayout from '../components/layout/MainLayout';
import RawApiRequest from '../components/admin/testTools/RawApiRequest';
import { useTenant } from '@libs/contexts/TenantContext';
import { toast } from 'react-toastify';
import { get as apiGet, post as apiPost } from '../api/apiClient';
import { buildWebhookTestPayload } from '@libs/schemas/webhookSchema';

// Import API definitions
import {
  userApiEndpoints,
  tenantApiEndpoints,
  alertApiEndpoints,
  actionApiEndpoints,
  eventApiEndpoints,
  webhookApiEndpoints,
  restActionApiEndpoints,
} from '../utils/apiEndpoints';

// Define API categories
const API_CATEGORIES = [
  { id: 'users', label: 'User Management', endpoints: userApiEndpoints },
  { id: 'tenants', label: 'Tenant Management', endpoints: tenantApiEndpoints },
  { id: 'alerts', label: 'Alert Management', endpoints: alertApiEndpoints },
  { id: 'actions', label: 'Action Management', endpoints: actionApiEndpoints },
  { id: 'events', label: 'Event Management', endpoints: eventApiEndpoints },
  { id: 'webhooks', label: 'Webhook Management', endpoints: webhookApiEndpoints },
  { id: 'restActions', label: 'REST Action Management', endpoints: restActionApiEndpoints },
  { id: 'rawApi', label: 'Raw API Request', component: RawApiRequest },
  { id: 'agentRest', label: 'Agent REST Command', component: RestCommandTool },
];

const AdminTestPage = () => {
  const { currentUser, userData, userClaims, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const { selectedTenant } = useTenant();
  const [activeCategory, setActiveCategory] = useState('users');
  const [authStatus, setAuthStatus] = useState({
    authenticated: false,
    username: '',
    role: '',
    permissions: [],
  });

  // Webhooks section state
  const [webhooksOpen, setWebhooksOpen] = useState(true);
  const [webTab, setWebTab] = useState('actions'); // 'actions' | 'inbound'
  const [whLoading, setWhLoading] = useState(false);
  const [whError, setWhError] = useState(null);
  const [whActions, setWhActions] = useState([]);
  const [testingId, setTestingId] = useState(null);
  // inbound tester
  const [routingKey, setRoutingKey] = useState('demo');
  const [inboundPayload, setInboundPayload] = useState('{"message": "Hello from Test API Tools"}');
  const [inboundSending, setInboundSending] = useState(false);
  const [inboundResult, setInboundResult] = useState(null);
  // Step 10: inbound secret status
  const [inboundSec, setInboundSec] = useState({ configured: null, loading: false });

  // Check if user has authentication
  useEffect(() => {
    console.log('[AdminTestPage] Checking auth:', { currentUser, userData, userClaims });

    if (!currentUser) {
      console.log('[AdminTestPage] Not authenticated, redirecting to login');
      navigate(ROUTES.LOGIN, { state: { from: window.location.pathname } });
      return;
    }

    // Verify admin permissions
    const userRole = userClaims?.role || userData?.role;
    const hasAdminAccess = userRole === ROLES.ADMIN || userRole === ROLES.TENANT_ADMIN;

    if (!hasAdminAccess && !isAdmin) {
      console.log('[AdminTestPage] User does not have admin access');
      navigate(ROUTES.DASHBOARD);
      return;
    }

    // Update auth status
    setAuthStatus({
      authenticated: true,
      username: currentUser.email || '',
      role: userRole || '',
      permissions: userClaims?.permissions || [],
    });

    console.log('User authenticated:', currentUser.email);
  }, [currentUser, userData, userClaims, isAdmin, navigate]);

  // Load webhook actions for current tenant
    const load = async () => {
      if (!selectedTenant?.id || !webhooksOpen) return;
      try {
        setWhLoading(true);
        setWhError(null);
        const t = encodeURIComponent(selectedTenant.id);
        let data;
        try {
          // Preferred path exposed by current backend
          data = await apiGet(`/api/v2/admin/webhooks?tenantId=${t}`);
        } catch (err) {
          const msg = String(err?.message || '');
          if (msg.includes('404')) {
            // Fallback to canonical '/list' variant if alias is present
            data = await apiGet(`/api/v2/admin/webhooks/list?tenantId=${t}`);
          } else {
            throw err;
          }
        }
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        setWhActions(list);
      } catch (e) {
        console.error('[API-Test][Webhooks] list error', e);
        setWhError(e?.message || 'Failed to load webhooks');
      } finally {
        setWhLoading(false);
      }
    };

  useEffect(() => {
    load();
  }, [selectedTenant, webhooksOpen]);

  // Step 10: load inbound secret status when switching to Inbound tab or tenant changes
  useEffect(() => {
    const fetchStatus = async () => {
      if (!selectedTenant?.id || webTab !== 'inbound') return;
      try {
        setInboundSec(s => ({ ...s, loading: true }));
        const res = await apiGet(`/api/v2/admin/tenants/${encodeURIComponent(selectedTenant?.id)}/webhooks/secret/status`);
        const configured = !!(res?.configured);
        setInboundSec({ configured, loading: false });
      } catch (e) {
        console.warn('[API-Test][Webhooks] secret status error', e);
        setInboundSec({ configured: null, loading: false });
      }
    };
    fetchStatus();
  }, [selectedTenant, webTab]);

  const canUseInbound = authStatus.role === ROLES.ADMIN || authStatus.role === ROLES.TENANT_ADMIN;

  // Step 10: ensure secret (admin only) — moved to component scope
  const handleEnsureSecret = async () => {
    if (!isAdmin) return;
    if (!selectedTenant?.id) return toast.warning('Select a tenant first');
    try {
      setInboundSec(s => ({ ...s, loading: true }));
      const res = await apiPost(`/api/v2/admin/tenants/${encodeURIComponent(selectedTenant.id)}/webhooks/secret`, { op: 'ensure' });
      const mode = res?.mode || 'exists';
      toast.success(`Secret ${mode}`);
    } catch (e) {
      console.error('[API-Test][Webhooks] ensure secret error', e);
      toast.error(e?.message || 'Ensure secret failed');
    } finally {
      // refresh status
      try {
        const st = await apiGet(`/api/v2/admin/tenants/${encodeURIComponent(selectedTenant.id)}/webhooks/secret/status`);
        setInboundSec({ configured: !!st?.configured, loading: false });
      } catch {
        setInboundSec({ configured: null, loading: false });
      }
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      toast.success('Copied actionId');
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleOutboundTest = async (action) => {
    if (!selectedTenant?.id) {
      toast.warning('Select a tenant first');
      return;
    }
    try {
      setTestingId(action.id);
      const payload = buildWebhookTestPayload(action.messageTemplate || '');
      const res = await apiPost('/api/v2/tenant/webhooks/dispatch', { actionId: action.id, payload });
      const r = res?.result || res || {};
      const attempts = r.attempts ?? 0;
      const latency = r.latencyMs ?? 0;
      const status = r.statusCode ?? r.status ?? 0;
      const preview = (r.responsePreview || '').toString().slice(0, 200);
      const ok = !!(r.success);
      console.log('[API-Test][Webhooks] outbound-test', { actionId: action.id, statusCode: status });
      if (ok) {
        toast.success(`Webhook OK — status ${status}, attempts ${attempts}, ${latency}ms, preview: ${preview}`);
      } else {
        toast.error(`Webhook failed — status ${status}, attempts ${attempts}, ${latency}ms`);
      }
    } catch (e) {
      console.error('[API-Test][Webhooks] outbound-test error', e);
      toast.error(e?.message || 'Outbound test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleInboundTest = async () => {
    if (!canUseInbound) return;
    if (!selectedTenant?.id) {
      toast.warning('Select a tenant first');
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(inboundPayload || '{}');
    } catch (e) {
      toast.error('Payload must be valid JSON');
      return;
    }
    try {
      setInboundSending(true);
      setInboundResult(null);
      const body = { tenantId: selectedTenant.id, routing_key: routingKey.trim(), payload: parsed };
      const res = await apiPost('/api/v2/admin/webhooks/inbound/test', body);
      const status = res?.statusCode ?? res?.status ?? 0;
      console.log('[API-Test][Webhooks] inbound-test', { routing_key: routingKey, statusCode: status });
      setInboundResult({ ok: !!res?.success, statusCode: status, bodyPreview: (res?.bodyPreview || '').toString().slice(0, 1024), reason: res?.reason });
      if (res?.success) toast.success(`Inbound test OK — ${status}`); else toast.error(`Inbound test failed — ${status}`);
    } catch (e) {
      console.error('[API-Test][Webhooks] inbound-test error', e);
      toast.error(e?.message || 'Inbound test failed');
    } finally {
      setInboundSending(false);
    }
  };

  return (
    <MainLayout pageTitle="API Test Console">
      <div className="container mx-auto px-4 py-4">
        {/* User Status Bar */}
        <div className="bg-blue-50 p-4 mb-6 rounded-lg shadow">
          <div className="flex justify-between">
            <h2 className="text-lg font-semibold mb-2">User Auth Status</h2>
            <button
              onClick={logout}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              Logout
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Email:</p>
              <p className="font-medium">{authStatus.username || 'Not logged in'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Role:</p>
              <p className="font-medium">{authStatus.role || 'None'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">User ID:</p>
              <p className="font-medium text-xs truncate">{currentUser?.uid || 'N/A'}</p>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p>
              <span className="font-semibold">Authentication Type:</span> Firebase
              {currentUser ? (
                <span className="text-green-500 ml-2">(✓ Verified)</span>
              ) : (
                <span className="text-red-500 ml-2">(✗ Invalid or Missing)</span>
              )}
            </p>
          </div>
        </div>

        {/* API Category Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {API_CATEGORIES.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeCategory === category.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {category.label}
              </button>
            ))}
          </nav>
        </div>

        {/* API Endpoint Testers */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold mb-4">
            {API_CATEGORIES.find(c => c.id === activeCategory)?.label || 'API Tests'}
          </h2>

          {(() => {
            const active = API_CATEGORIES.find(c => c.id === activeCategory);
            if (active?.component) {
              const Component = active.component;
              return <Component />;
            }
            return active?.endpoints.map(endpoint => (
              <ApiTester key={`${endpoint.method}-${endpoint.path}`} endpoint={endpoint} />
            ));
          })()}
        </div>

        {/* Webhooks (collapsible) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
          <button
            className="w-full flex items-center justify-between px-6 py-4"
            onClick={() => setWebhooksOpen(!webhooksOpen)}
            aria-expanded={webhooksOpen}
          >
            <span className="text-lg font-semibold">Webhooks</span>
            <span className="text-gray-500">{webhooksOpen ? '▾' : '▸'}</span>
          </button>
          {webhooksOpen && (
            <div className="px-6 pb-6">
              {/* Tabs */}
              <div className="mb-4 border-b border-gray-200">
                <nav className="-mb-px flex space-x-6">
                  <button
                    onClick={() => setWebTab('actions')}
                    className={`py-2 px-1 border-b-2 text-sm ${webTab === 'actions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    Actions
                  </button>
                  <button
                    onClick={() => setWebTab('inbound')}
                    className={`py-2 px-1 border-b-2 text-sm ${webTab === 'inbound' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    Inbound Tester
                  </button>
                </nav>
              </div>

              {webTab === 'actions' && (
                <div>
                  {!selectedTenant?.id && (
                    <div className="p-3 bg-yellow-50 text-yellow-700 border border-yellow-200 mb-3 text-sm">Select a tenant to load webhook actions.</div>
                  )}
                  {whError && (
                    <div className="p-3 bg-red-50 text-red-700 border border-red-200 mb-3 text-sm">{whError}</div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 table-fixed">
                      <colgroup>
                        <col className="w-1/5" />
                        <col />
                        <col className="w-28" />
                        <col className="w-56" />
                        <col className="w-56" />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action ID</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {whLoading ? (
                          <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-500">Loading...</td></tr>
                        ) : whActions.length === 0 ? (
                          <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-500">No webhook actions</td></tr>
                        ) : whActions.map((a) => {
                          const enabled = a.enabled !== false;
                          const statusPill = enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
                          const url = a.url || a.webhookUrl || '';
                          const id = a.id || a.actionId || a.uid;
                          const label = a.label || id || '—';
                          return (
                            <tr key={id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-700">{label}</td>
                              <td className="px-4 py-2 text-sm text-blue-700 truncate" title={url}>{url}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusPill}`}>
                                  {enabled ? 'enabled' : 'disabled'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-600">{id}</td>
                              <td className="px-4 py-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleOutboundTest({ ...a, id })}
                                    disabled={testingId === id}
                                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                                  >
                                    {testingId === id ? 'Testing…' : 'Test'}
                                  </button>
                                  <button
                                    onClick={() => handleCopy(id)}
                                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
                                  >
                                    Copy
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {webTab === 'inbound' && (
                <div>
                  {!canUseInbound && (
                    <div className="p-3 bg-yellow-50 text-yellow-700 border border-yellow-200 mb-3 text-sm">You do not have permission to use the inbound tester.</div>
                  )}
                  {/* Step 10: secret status / ensure control */}
                  <div className="mb-3 text-xs text-gray-600 flex items-center gap-3">
                    {inboundSec.loading ? (
                      <span>Checking inbound secret…</span>
                    ) : inboundSec.configured === true ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800">Configured ✓</span>
                    ) : inboundSec.configured === false ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">Not configured</span>
                        {isAdmin && (
                          <button onClick={handleEnsureSecret} className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50">Ensure Secret</button>
                        )}
                      </>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">routing_key</label>
                      <input
                        type="text"
                        value={routingKey}
                        onChange={(e) => setRoutingKey(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., teams, slack, github"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payload (JSON)</label>
                      <textarea
                        rows="6"
                        value={inboundPayload}
                        onChange={(e) => setInboundPayload(e.target.value)}
                        className="w-full p-2 border rounded font-mono text-xs"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={handleInboundTest}
                      disabled={!canUseInbound || inboundSending}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                    >
                      {inboundSending ? 'Sending…' : 'Send inbound (admin)'}
                    </button>
                  </div>
                  {inboundResult && (
                    <div className="mt-3 text-sm text-gray-700">
                      <div className="mb-1">Status: {inboundResult.statusCode}</div>
                      <div className="mb-1">Result: {inboundResult.ok ? 'success' : 'failure'} {inboundResult.reason ? `— ${inboundResult.reason}` : ''}</div>
                      <div className="mb-1 font-medium">Body Preview</div>
                      <pre className="p-2 bg-gray-50 border rounded overflow-x-auto text-xs">{inboundResult.bodyPreview || '—'}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};
export default AdminTestPage;
